import { Controller } from '@hotwired/stimulus'

import { closeFlashAlert, closeFlashNotice, flashAlert, flashNotice } from '../../shared/flash'
export default class extends Controller {
  static targets = [
    'container',
    'loadingContainer',
    'productContainer',
    'scheduleContainer',
    'layoutContainer',
    'addToCartContainer',
    'addToCartBtn',
    'quantity'
  ];

  initialize() {
    this.orderId = parseInt(this?.containerTarget?.dataset?.orderId);
    this.destinationId = parseInt(this?.containerTarget?.dataset?.destinationId);
    this.product = null;
    this.quantity = 1;
    this.availabilityIds = []
    this.inventoryUnitIds = []
    this.deliveryAddress = null
  }

  connect() {
    this.fetchHeaders = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    }

    if (this.destinationId) {
      const detail = { destinationId: this.destinationId }
      const destinationSelectEvent = new CustomEvent('destinationSelect', { detail })
      document.dispatchEvent(destinationSelectEvent)
    }

    document.addEventListener('orderLoaded', this.onOrderLoad);
    document.addEventListener('destinationSelect', this.onDestinationSelect)
    document.addEventListener('productSelect', this.onProductSelect)
    document.addEventListener('quantitySelected', this.onQuantitySelected)
    document.addEventListener('availabilitySelected', this.onAvailabilitiesSelected)
    document.addEventListener('scheduleContinue', this.onScheduleContinue)
    document.addEventListener('positionSelected', this.onPositionSelected);
  }

  resetFormInfo() {
    this.product = null;
    this.quantity = 1;
    this.availabilityIds = []
    this.inventoryUnitIds = []

    this.addToCartBtnTarget.setAttribute('disabled', true)
    this.layoutContainerTarget.classList.add('hidden')
    this.scheduleContainerTarget.classList.add('hidden')
  }

  startLoading() {
    closeFlashAlert()
    closeFlashNotice()
    this.addToCartBtnTarget.classList.add('hidden')
    this.loadingContainerTarget.classList.remove('hidden')
  }

  stopLoading() {
    this.addToCartBtnTarget.classList.remove('hidden')
    this.loadingContainerTarget.classList.add('hidden')
    this.validateForm()
  }

  onOrderLoad = ({ detail }) => {
    this.order = detail?.order;
    this.orderId = detail?.order?.id;
    this.destinationId = detail?.order?.destination_id
  }

  onProductSelect = event => {
    this.resetFormInfo()
    this.product = event.detail.product
    this.destinationId = event.detail.destinationId
    this.addToCartBtnTarget.setAttribute('disabled', true)

    if (this.product.layout) {
      this.addToCartContainerTarget.classList.add('hidden')
      this.scheduleContainerTarget.classList.remove('hidden')
    } else if (this.product.is_rental) {
      this.scheduleContainerTarget.classList.remove('hidden')
      this.addToCartContainerTarget.classList.remove('hidden')
    } else {
      this.scheduleContainerTarget.classList.remove('hidden')
      this.addToCartContainerTarget.classList.remove('hidden')
    }

    this.validateForm();
  }

  onDestinationSelect = event => {
    this.resetFormInfo()

    this.destinationId = event?.detail?.destinationId || this?.destination?.id
    this.deliveryAddress = event?.detail?.deliveryAddress
  }

  onQuantitySelect = event => {
    this.quantity = parseInt(event?.detail?.quantity) || 1
    this.validateForm();
  }

  onScheduleContinue = event => {
    this.availabilityIds = event?.detail?.availabilityIds
    this.addToCartContainerTarget.classList.remove('hidden');

    if (this.product?.layout) {
      this.scheduleContainerTarget.classList.add('hidden')
      this.layoutContainerTarget.classList.remove('hidden');
      this.addToCartBtnTarget.setAttribute('disabled', true)
    }
    this.validateForm();
  }

  onAvailabilitiesSelected = event => {
    const availabilities = event?.detail?.availabilities;
    this.availabilities = availabilities
    this.availabilityIds = availabilities.map(a => a.id)

    if (this.product?.layout) {
      return
    }

    this.addToCartContainerTarget.classList.remove('hidden');

    if (this.availabilityIds.length) {
      this.addToCartBtnTarget.removeAttribute('disabled')
    } else {
      this.addToCartBtnTarget.setAttribute('disabled', true)
    }

    this.validateForm();
  }

  onQuantitySelected = event => {
    this.quantity = parseInt(event?.detail?.quantity)
    this.validateForm()
  }

  onPositionSelected = event => {
    this.inventoryUnitIds = event?.detail?.inventoryUnitIds

    if (this.inventoryUnitIds.length) {
      this.addToCartBtnTarget.removeAttribute('disabled')
    } else {
      this.addToCartBtnTarget.setAttribute('disabled', true)
    }

    this.validateForm()
  }

  validateForm = () => {
    let check = !!this.product
    const quantity = this.quantity

    if (!this.product?.is_rental) {
      check = check && quantity > 0
    } else if (this.product?.layout) {
      check = check && this.availabilityIds.length > 0 && this.inventoryUnitIds.length > 0
    } else {
      check = check && this.availabilityIds.length > 0 && quantity > 0
    }

    if (check) {
      this.enableAddToCart()
    } else {
      this.disableAddToCart()
    }
  }

  onOrderError = error => {
    console.error(error);
    const message = error?.message || error?.error || 'Something went wrong, please try again.'
    flashAlert(message);
  }

  enableAddToCart = () => {
    this.addToCartContainerTarget.classList.remove('hidden');
    this.addToCartBtnTarget.removeAttribute('disabled');
  }

  disableAddToCart = () => {
    this.addToCartBtnTarget.setAttribute('disabled', true);
  }

  addToCart(event) {
    event.preventDefault();
    this.startLoading()
    if (this.product.is_rental) {
      this.makeRentalBooking();
    } else {
      this.makeRetailBooking();
    }
  }

  cartAddSuccess = response => {
    if (!this.destinationId && this.response[0]?.destination_id) {
      this.destinationId = response[0]?.destination_id
      this.containerTarget.dataset.destinationId = response[0]?.destination_id
    }

    flashNotice('The product was successfully added to the cart.');
    const detail = {
      orderId: this.orderId,
      order: this.order,
      destinationId: this.destinationId,
    }
    const addToCartEvent = new CustomEvent('cartAddSuccess', { detail });
    document.dispatchEvent(addToCartEvent);
    this.resetFormInfo()
  }

  makeRentalBooking() {
    const bookingParams = {
      booking: {
        order_id: this.orderId,
        quantity: this.quantity || 1,
        inventory_unit_ids: this.inventoryUnitIds,
        availability_ids: this.availabilityIds,
      },
      delivery_address: this.deliveryAddress
    };

    fetch(
      `/sandbar/admin/orders/${this.orderId}/create_booking`,
      {
        method: 'POST',
        body: JSON.stringify(bookingParams),
        headers: this.fetchHeaders,
      }
    )
      .then(resp => {
        this.stopLoading()
        if (resp.ok) {
          resp.json().then(booking => {
            this.cartAddSuccess(booking)
          })
        }
        resp.json().then(err => this.onOrderError(err))
      })
      .catch(err => this.onOrderError(err));
  }

  makeRetailBooking() {
    const itemParams = {
      order_id: this.orderId,
      destination_id: this.destinationId,
      product_id: this.product.id,
      quantity: this.quantity || 1,
      delivery_address: this.deliveryAddress
    };

    fetch(
      `/sandbar/admin/orders/${this.orderId}/add_item`,
      {
        method: 'POST',
        body: JSON.stringify(itemParams),
        headers: this.fetchHeaders,
      }
    )
      .then(resp => {
        this.stopLoading()
        if (resp.ok) {
          resp.json().then(line_item => {
            this.cartAddSuccess(line_item)
          })
        } else {
          resp.json().then(err => this.onOrderError(err))
        }
      })
      .catch(err => this.onOrderError(err))
  }
}
